'use client'

/*
 * The non-lava, non-plain alternates. All pure CSS — cheap, no shader — so they
 * ship in the main chunk. Selected by `background.effect`; each reads only its
 * own config block. Mutually exclusive by construction: SiteBackground renders
 * exactly one of these, so unselected layers are absent from the DOM entirely
 * (a stronger guarantee than the prototype's `display:none`).
 */

import React from 'react'

import type { BackgroundConfig } from '@/site'

import styles from './background.module.css'

interface EffectLayerProps {
  effect: 'aurora' | 'grid' | 'dots' | 'topo'
  config: BackgroundConfig
}

export function EffectLayer({ effect, config }: EffectLayerProps) {
  if (effect === 'grid' && config.grid) {
    const { cellSize, lineWidth, color } = config.grid
    return (
      <div
        className={styles.effect}
        style={{
          backgroundImage: `linear-gradient(to right, ${color} ${lineWidth}px, transparent ${lineWidth}px), linear-gradient(to bottom, ${color} ${lineWidth}px, transparent ${lineWidth}px)`,
          backgroundSize: `${cellSize}px ${cellSize}px`,
        }}
      />
    )
  }

  if (effect === 'dots' && config.dots) {
    const { spacing, radius, color } = config.dots
    return (
      <div
        className={styles.effect}
        style={{
          backgroundImage: `radial-gradient(${color} ${radius}px, transparent ${radius + 0.5}px)`,
          backgroundSize: `${spacing}px ${spacing}px`,
        }}
      />
    )
  }

  if (effect === 'topo' && config.topo) {
    const { amplitude, color } = config.topo
    const band = Math.max(6, Math.round(amplitude))
    return (
      <div
        className={styles.effect}
        style={{
          backgroundImage: `repeating-radial-gradient(circle at 50% 120%, transparent 0, transparent ${band - 1}px, ${color} ${band - 1}px, ${color} ${band}px)`,
        }}
      />
    )
  }

  if (effect === 'aurora' && config.aurora) {
    const [c0 = '#0f5132', c1 = '#198754', c2 = '#20c997'] = config.aurora.colors
    return (
      <div className={styles.aurora}>
        <span style={{ background: `radial-gradient(circle, ${c0} 0%, transparent 70%)` }} />
        <span style={{ background: `radial-gradient(circle, ${c1} 0%, transparent 70%)` }} />
        <span style={{ background: `radial-gradient(circle, ${c2} 0%, transparent 70%)` }} />
      </div>
    )
  }

  return null
}

export default EffectLayer
